import { type JSX } from 'react';
import { generatePath } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE } from 'Config/Icons';
import { TPRM_ASSESSMENT_HISTORICAL, UCRM_ASSESSMENT_HISTORICAL } from 'Config/PathTemplates';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceWorkflowArchive, getArchivedAssessmentSubject, getArchivedOrganization } from 'Models/UcrmTprm';

import styles from './ArchivedDueDiligenceAssessmentsModal.module.css';

export interface ArchivedDueDiligenceAssessmentsModalProps {
    hideModal: () => void;
    module: DueDiligenceModule;
    workflowArchive: TprmOrUcrmDueDiligenceWorkflowArchive;
}

/**
 * Displays a modal with a list of assessments from a historical due diligence workflow (`workflowArchive`).
 * Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard to allow users to view and navigate to individual assessments from past workflow snapshots.
 * When an assessment name is clicked, the user is routed to the historical assessment detail page.
 */
export const ArchivedDueDiligenceAssessmentsModal = (props: ArchivedDueDiligenceAssessmentsModalProps): JSX.Element => {
    return (
        <Modal open header="Assessments">
            <ul className={styles.list}>
                {[...props.workflowArchive.assessments]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((assessment) => (
                        <li key={assessment.id}>
                            <Link
                                size="sm"
                                to={generatePath(props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_HISTORICAL : TPRM_ASSESSMENT_HISTORICAL, {
                                    timestamp: props.workflowArchive.timestamp,
                                    third_party_id: getArchivedOrganization(props.module, props.workflowArchive).id,
                                    service_id: getArchivedAssessmentSubject(props.module, props.workflowArchive).id,
                                    assessment_id: assessment.id,
                                })}
                            >
                                {assessment.name}
                            </Link>
                        </li>
                    ))}
            </ul>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};
